import { PinnedNavItem } from '@certhon/user-settings';
import { useCallback, useMemo } from 'react';

import { useIsAllowed } from '@/app/navigation/access';
import {
  useUpdateUserSettings,
  useUserSettings,
} from '@/features/user-settings/user-settings.hooks';

/** Default landing page when the user has not pinned anything. */
export const DEFAULT_HOME_URL = '/projects';

/**
 * Sidebar pins, persisted in the user's server-side settings.
 *
 * The first pin doubles as the user's home page — the app root redirects there
 * — which is why reordering and "make home" are the same operation.
 */
export function usePinnedNav() {
  const settings = useUserSettings();
  const { canEdit, updateSettings } = useUpdateUserSettings();
  const { isUrlAllowed } = useIsAllowed();

  // Pins to screens this account may not open stay in the settings (an
  // account could in principle change kind) but are neither shown nor used
  // as home page.
  const pins = useMemo(
    () => settings.pinned_nav_items.filter((pin) => isUrlAllowed(pin.url)),
    [settings.pinned_nav_items, isUrlAllowed],
  );

  const write = useCallback(
    (next: (current: PinnedNavItem[]) => PinnedNavItem[]) => {
      updateSettings((current) => ({
        pinned_nav_items: next(current.pinned_nav_items),
      }));
    },
    [updateSettings],
  );

  const pinnedUrls = useMemo(() => new Set(pins.map((p) => p.url)), [pins]);

  const isPinned = useCallback(
    (url?: string) => !!url && pinnedUrls.has(url),
    [pinnedUrls],
  );

  const pin = useCallback(
    (item: PinnedNavItem) =>
      write((current) => [...current.filter((p) => p.url !== item.url), item]),
    [write],
  );

  const unpin = useCallback(
    (url: string) => write((current) => current.filter((p) => p.url !== url)),
    [write],
  );

  const togglePin = useCallback(
    (item: PinnedNavItem) => (isPinned(item.url) ? unpin(item.url) : pin(item)),
    [isPinned, pin, unpin],
  );

  /**
   * Store a whole new order, given as urls (drag & drop hands back the order
   * it settled on). Pins missing from `urls` keep their relative place at the
   * end, so a stale drag can never drop one.
   */
  const setPinOrder = useCallback(
    (urls: string[]) =>
      write((current) => {
        const ranked = urls
          .map((url) => current.find((p) => p.url === url))
          .filter((p): p is PinnedNavItem => !!p);
        const rest = current.filter((p) => !urls.includes(p.url));
        return [...ranked, ...rest];
      }),
    [write],
  );

  /** Nudge a pin one slot up or down (keyboard / context menu). */
  const movePin = useCallback(
    (url: string, delta: -1 | 1) =>
      write((current) => {
        const from = current.findIndex((p) => p.url === url);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= current.length) return current;
        const next = [...current];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      }),
    [write],
  );

  /** Promote a pin to first position, making it the home page. */
  const makeHome = useCallback(
    (url: string) =>
      write((current) => {
        const item = current.find((p) => p.url === url);
        if (!item) return current;
        return [item, ...current.filter((p) => p.url !== url)];
      }),
    [write],
  );

  return {
    pins,
    /** False while offline: settings writes are not queued, see the hook. */
    canEdit,
    isPinned,
    pin,
    unpin,
    togglePin,
    setPinOrder,
    movePin,
    makeHome,
    /** Where the app root should send this user. */
    homeUrl: pins[0]?.url ?? DEFAULT_HOME_URL,
  };
}
